import { FC, useMemo } from 'react';
import { SubmitButton } from '@/components/form/submit-button';
import { Button } from '@/components/ui/button';
import { strings } from '@/constants/strings';
import { useForm } from '@/forms/hooks/use-form';
import { AssignDeviceFieldValues } from '@/forms/schemas/assign-device/field-values';
import { buildAssignDeviceFormSchema } from '@/forms/schemas/assign-device/schema';
import { useOrganisationSelectOptions } from '@/hooks/use-organisation-select-options';
import { DrawerContentFormLayout } from '@/layouts/drawer-content-form-layout';
import { AnyDevice } from '@/types/device';
import { Organisation } from '@/types/organisation';

export type AssignDevicesFormProps = {
  devices: AnyDevice[];
  organisations: Organisation[];
  onCancel: () => void;
  onSubmit: (values: AssignDeviceFieldValues) => void;
};

export const AssignDevicesForm: FC<AssignDevicesFormProps> = ({
  devices,
  organisations,
  onCancel,
  onSubmit,
}) => {
  const organisationOptions = useOrganisationSelectOptions(organisations);

  const currentOrganisationId = useMemo(() => {
    const firstDeviceOrganisationId = devices[0]?.organisation?.id;
    const allDevicesHaveSameOrganisation = devices.every(
      (device) => device.organisation?.id === firstDeviceOrganisationId,
    );
    return allDevicesHaveSameOrganisation
      ? firstDeviceOrganisationId
      : undefined;
  }, [devices]);

  const formSchema = useMemo(
    () =>
      buildAssignDeviceFormSchema({
        currentOrganisationId,
        organisationOptions,
      }),
    [currentOrganisationId, organisationOptions],
  );

  const { fieldItems, handleSubmit } = useForm({
    schema: formSchema,
    onSubmit,
  });

  return (
    <DrawerContentFormLayout
      buttonItems={[
        <Button key="cancel" onClick={onCancel} variant="secondary">
          {strings.devices.drawer_assign.cancel}
        </Button>,
        <SubmitButton
          key="submit"
          text={strings.devices.drawer_assign.submit}
        />,
      ]}
      fieldItems={fieldItems}
      onSubmit={handleSubmit}
    />
  );
};
